import React, { FC, useEffect, useState } from 'react';
import Modal from 'components/shared/Modal';
import { useTranslation } from 'react-i18next';
import { Button } from '@kontentino/ui';
import { WidgetsSetup } from 'components/posts/detail/layout/widgetsSetup/WidgetsSetupProvider';
import WidgetsSetupModalReordeableList from 'components/posts/detail/layout/widgetsSetup/widgetsSetupModal/WidgetsSetupModalReordeableList';
import clsx from 'clsx';

type Props = {
  open: boolean;
  onClose: () => void;
  activeWidgetsSetup: WidgetsSetup[] | null;
  defaultWidgetsSetup: WidgetsSetup[];
  onWidgetsSetupSubmit: (widgetsSetup: WidgetsSetup[] | null) => void;
};

const WidgetsSetupModal: FC<Props> = ({
  open,
  onClose,
  activeWidgetsSetup,
  defaultWidgetsSetup,
  onWidgetsSetupSubmit,
}) => {
  const { t } = useTranslation();
  const [modalRoot] = useState(() => document.getElementById('modal-root'));
  const [widgetsSetup, setWidgetsSetup] = useState<WidgetsSetup[]>(
    activeWidgetsSetup ?? defaultWidgetsSetup,
  );

  useEffect(() => {
    if (!open) {
      setWidgetsSetup(activeWidgetsSetup ?? defaultWidgetsSetup);
    }
  }, [defaultWidgetsSetup, activeWidgetsSetup, open]);

  function onSubmit() {
    onWidgetsSetupSubmit(widgetsSetup);
    onClose();
  }

  const onCancel = () => {
    onClose();
    setWidgetsSetup(activeWidgetsSetup ?? defaultWidgetsSetup);
  };

  const onClearToDefault = () => {
    setWidgetsSetup(defaultWidgetsSetup);
    if (isWidgetSetupModifiedExternally) {
      onWidgetsSetupSubmit(null);
      onClose();
    }
  };

  const isWidgetSetupModifiedExternally = !!activeWidgetsSetup;
  const isWidgetSetupModifiedLocally =
    JSON.stringify(widgetsSetup) !== JSON.stringify(defaultWidgetsSetup);
  const isWidgetSetupModified =
    isWidgetSetupModifiedExternally || isWidgetSetupModifiedLocally;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      classNames={{
        modal: '!tw-max-w-[25rem]',
      }}
    >
      <Modal.Header
        title={t('widgetsSetup.modal.title')}
        subtitle={t('widgetsSetup.modal.description')}
        className="tw-pb-4"
      />
      <Modal.Content>
        <WidgetsSetupModalReordeableList
          value={widgetsSetup}
          onChange={setWidgetsSetup}
          portalRoot={modalRoot}
        />
      </Modal.Content>
      <Modal.Footer
        withDivider
        className={clsx({ 'tw-justify-between': isWidgetSetupModified })}
      >
        {!!isWidgetSetupModified && (
          <Button variant="ghost" onClick={onClearToDefault}>
            {t('clearToDefault')}
          </Button>
        )}
        <div className="tw-flex tw-items-center tw-gap-3">
          <Button variant="secondary" onClick={onCancel}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            {t('save')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default WidgetsSetupModal;
